/**
 * ClubLanding — ekran logowania/rejestracji (publiczny, niezalogowany).
 * Tabs: Logowanie | Rejestracja
 */
import React, { useState } from 'react';
import { clubApi, formatApiErrorDetail } from './clubApi';

export default function ClubLanding({ onAuthenticated }) {
  const [tab, setTab] = useState('login'); // login | register
  return (
    <div className="club-shell">
      <div className="club-brand">
        <div className="club-brand-logo">🥃</div>
        <h1 className="club-brand-name">MR JERRY</h1>
        <div className="club-brand-sub">Club · Members Only</div>
      </div>

      <div className="club-card">
        <div className="club-tabs">
          <button
            className={`club-tab ${tab === 'login' ? 'active' : ''}`}
            onClick={() => setTab('login')}
            data-testid="club-tab-login"
          >Logowanie</button>
          <button
            className={`club-tab ${tab === 'register' ? 'active' : ''}`}
            onClick={() => setTab('register')}
            data-testid="club-tab-register"
          >Rejestracja</button>
        </div>

        {tab === 'login'
          ? <LoginForm onAuthenticated={onAuthenticated} />
          : <RegisterForm onAuthenticated={onAuthenticated} />}
      </div>

      <p className="club-help" style={{ textAlign: 'center', marginTop: 24 }}>
        Wpadnij na pizzę albo koktajl, zeskanuj QR z paragonu i odbieraj punkty.
        <br />Im więcej punktów, tym lepsze nagrody.
      </p>
    </div>
  );
}

// ============== LOGIN ==============
function LoginForm({ onAuthenticated }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const { data } = await clubApi.post('/auth/login', { email, password });
      onAuthenticated(data.member);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="club-login-form">
      {error && <div className="club-alert club-alert-error" data-testid="club-login-error">⚠️ {error}</div>}
      <label className="club-label">E-mail</label>
      <input
        className="club-input"
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="ty@example.com"
        autoComplete="email"
        data-testid="club-login-email"
      />
      <div style={{ height: 12 }} />
      <label className="club-label">Hasło</label>
      <input
        className="club-input"
        type="password"
        required
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="••••••••"
        autoComplete="current-password"
        data-testid="club-login-password"
      />
      <div style={{ height: 20 }} />
      <button
        type="submit"
        className="club-btn club-btn-primary"
        disabled={busy || !email || !password}
        data-testid="club-login-submit"
      >
        {busy ? 'Logowanie…' : '🥃 Wejdź do klubu'}
      </button>
    </form>
  );
}

// ============== REGISTER ==============
function RegisterForm({ onAuthenticated }) {
  const [form, setForm] = useState({
    firstName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [consents, setConsents] = useState({
    terms: false,
    rodo: false,
    marketing_sms: false,
    marketing_email: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updateConsent = (k) => (e) => setConsents((c) => ({ ...c, [k]: e.target.checked }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 6) { setError('Hasło min. 6 znaków'); return; }
    if (form.password !== form.confirmPassword) { setError('Hasła nie pasują'); return; }
    if (!consents.terms || !consents.rodo) { setError('Wymagana zgoda na regulamin i RODO'); return; }
    setBusy(true);
    try {
      const { data } = await clubApi.post('/auth/register', {
        email: form.email,
        password: form.password,
        firstName: form.firstName,
        phone: form.phone,
        consents,
      });
      // RODO audit trail
      const API = process.env.REACT_APP_BACKEND_URL;
      const axios = (await import('axios')).default;
      axios.post(`${API}/api/legal/consents`, {
        context: 'form:club-register',
        documentTypes: ['regulamin', 'privacy'],
        userEmail: form.email,
        userPhone: form.phone,
        marketingConsent: !!consents.marketing,
        formPayloadSummary: form.firstName,
      }).catch(() => {});
      onAuthenticated(data.member);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="club-register-form">
      {error && <div className="club-alert club-alert-error" data-testid="club-register-error">⚠️ {error}</div>}

      <label className="club-label">Imię</label>
      <input className="club-input" required value={form.firstName}
        onChange={update('firstName')} placeholder="Jan" data-testid="club-register-firstName" />
      <div style={{ height: 12 }} />

      <label className="club-label">E-mail</label>
      <input className="club-input" type="email" required value={form.email}
        onChange={update('email')} placeholder="ty@example.com" autoComplete="email"
        data-testid="club-register-email" />
      <div style={{ height: 12 }} />

      <label className="club-label">Telefon</label>
      <input className="club-input" type="tel" required value={form.phone}
        onChange={update('phone')} placeholder="+48 500 500 500" autoComplete="tel"
        data-testid="club-register-phone" />
      <div style={{ height: 12 }} />

      <label className="club-label">Hasło</label>
      <input className="club-input" type="password" required value={form.password}
        onChange={update('password')} placeholder="min. 6 znaków" autoComplete="new-password"
        data-testid="club-register-password" />
      <div style={{ height: 12 }} />

      <label className="club-label">Powtórz hasło</label>
      <input className="club-input" type="password" required value={form.confirmPassword}
        onChange={update('confirmPassword')} placeholder="powtórz hasło" autoComplete="new-password"
        data-testid="club-register-password2" />
      <div style={{ height: 18 }} />

      <label className="club-check">
        <input type="checkbox" checked={consents.terms} onChange={updateConsent('terms')}
          data-testid="club-consent-terms" />
        <span className="club-check-text">
          Akceptuję <a href="/mrjerryclub/regulamin" target="_blank" className="club-link">Regulamin programu</a>
          <span className="club-check-required"> *</span>
        </span>
      </label>

      <label className="club-check">
        <input type="checkbox" checked={consents.rodo} onChange={updateConsent('rodo')}
          data-testid="club-consent-rodo" />
        <span className="club-check-text">
          Wyrażam zgodę na przetwarzanie danych osobowych (RODO)
          <span className="club-check-required"> *</span>
        </span>
      </label>

      <label className="club-check">
        <input type="checkbox" checked={consents.marketing_email} onChange={updateConsent('marketing_email')}
          data-testid="club-consent-email" />
        <span className="club-check-text">
          Chcę otrzymywać oferty i nowinki na e-mail (opcjonalnie)
        </span>
      </label>

      <label className="club-check">
        <input type="checkbox" checked={consents.marketing_sms} onChange={updateConsent('marketing_sms')}
          data-testid="club-consent-sms" />
        <span className="club-check-text">
          Chcę otrzymywać oferty SMS-em (opcjonalnie)
        </span>
      </label>

      <div style={{ height: 18 }} />

      <button
        type="submit"
        className="club-btn club-btn-primary"
        disabled={busy}
        data-testid="club-register-submit"
      >
        {busy ? 'Tworzenie konta…' : '✨ Załóż konto i odbierz 50 pkt'}
      </button>
    </form>
  );
}
